import pick from "lodash/pick";
import pickBy from "lodash/pickBy";
import get from "lodash/get";

import { getCustomFieldNames } from "../../utils/customAttributes";
import { getLocaleFromPathname } from "../../utils/url";
import { capturesShopperAnswers } from "../../utils/customAttributeScope";

/**
 * Actions whose payload is sent to `getReferral`, and therefore to
 * `/share/referral/short`.
 *
 * That endpoint copies a fixed allowlist onto `referral.advocate` and drops
 * everything else, so the merchant's fields have to travel GROUPED as well as
 * flat — see the note on `customAttributes` below. The share endpoint has no
 * such allowlist, so `getShare` callers need only the flat copy.
 *
 * The two wallet preloads are the ones easily missed: they call
 * `this.props.getReferral` directly (withActions ~951 and ~988) under their own
 * action names, so keying the grouping on `"getReferral"` alone left a
 * logged-out shopper's answers flat — and therefore dropped — on any widget
 * carrying a Google Pay or Apple Wallet button.
 */
export const REFERRAL_REQUEST_ACTIONS = [
  "getReferral",
  "googleWalletLinkPreload",
  "appleWalletDownloadPreload",
];

export interface BuildActionParamsInput {
  /** The action being dispatched, as `runAction` received it. */
  actionName: string;
  /** The widget's collected form state. */
  data: Record<string, unknown>;
  /** Widget config, for resolving which inputs are merchant-defined. Typed to
      match `getCustomFieldNames`, which indexes it. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config: { [index: string]: any };
  /** Interpolated action arguments. */
  values: Record<string, unknown>;
  widgetId: string;
  merchantId: string;
  campaignId: string;
}

/**
 * The payload a widget action is dispatched with.
 *
 * Extracted from `runAction` so it can be tested directly. Nothing exercised
 * this assembly before, and that is precisely how ENH-344's logged-in-advocate
 * rule shipped broken: `capturesShopperAnswers` was unit-tested in isolation and
 * passed every case, while the payload built here still carried the advocate's
 * answers — because the rule was consulted with action names that the real share
 * channels never use. A green predicate test said nothing about the payload.
 * These are the assertions that would have caught it.
 */
export function buildActionParams({
  actionName,
  data,
  config,
  values,
  widgetId,
  merchantId,
  campaignId,
}: BuildActionParamsInput): Record<string, unknown> {
  const hasCustomerId = Boolean(get(data, "customer.id"));
  const capturesAnswers = capturesShopperAnswers({ actionName, hasCustomerId });

  const optionalParams = pick(data, [
    "mobileNumber",
    "subscribe",
    "subscribeSms",
    // ENH-344: the "known field" components store their value at the top level
    // of `data` (birthday as a {day,month,year} object, zipCode as a string).
    // Forwarded so they ride at the top of the event payload, where fbt-account
    // promotes them to standard customer fields.
    //
    // Gated with the custom fields, because they are the same thing to the
    // shopper — answers they typed into this widget.
    ...(capturesAnswers ? ["birthday", "zipCode"] : []),
  ]);

  // ENH-344: forward merchant-defined custom input fields. Their values live in
  // `data` keyed by the field's configured name but are otherwise dropped, since
  // `optionalParams` only picks the built-in fields. The names are resolved from
  // the widget config's input components (excluding built-in names) so no
  // internal/system `data` keys leak into the payload.
  const customAttributeParams = pickBy(
    pick(data, getCustomFieldNames(config)),
    (value) => value !== "" && value != null
  );

  const carriedCustomAttributes = capturesAnswers ? customAttributeParams : {};

  const actionParams: Record<string, unknown> = {
    ...optionalParams,
    ...carriedCustomAttributes,
    ...values,
    widgetConfigId: widgetId,
    merchantId,
    campaignId,
    widgetId,
    // ENH-344: the same custom fields again, grouped, for the referral call
    // only.
    //
    // They are already spread flat above, and the share endpoint stores them
    // that way. `/share/referral/short` cannot: it copies a fixed allowlist of
    // keys onto `referral.advocate` (getAdvocateInfo in fbt-attribution) and
    // drops everything else, because the whole request body arrives as one bag —
    // widening that blindly would stamp `campaignId`, `widgetId`, `seed` and
    // friends onto the advocate record, and from there onto the email capture,
    // the Customer Service tab and a DSAR.
    //
    // Sending them grouped as well lets that endpoint take exactly the
    // merchant's fields without a denylist it would have to keep in step with.
    // Only on this action: the share path works today on the flat copy, and a
    // nested object inside `share.attributes` risks an index mapping conflict.
    ...(REFERRAL_REQUEST_ACTIONS.indexOf(actionName) > -1 &&
    Object.keys(carriedCustomAttributes).length
      ? { customAttributes: carriedCustomAttributes }
      : undefined),
  };

  if (REFERRAL_REQUEST_ACTIONS.indexOf(actionName) > -1) {
    // ENH-411: the host page's locale (`/en-de/refer-a-friend` -> `en-de`), for
    // the referral call. An advocate's newsletter opt-in rides on that call, and
    // the email capture fbt-account builds from it is what fbt-webhook sends On
    // Running, whose mutation picks the market from this locale. ENH-359 sends
    // the same value on the friend widget's email_capture event.
    //
    // Referral actions only: `/share` keeps unknown keys in `share.attributes`,
    // which a DSAR exports as merchant custom attributes, and it does not create
    // the capture anyway.
    //
    // Read from the page, never from the action's values, which could otherwise
    // override it, and omitted rather than sent empty when the path carries no
    // locale.
    delete actionParams.pageLocale;
    const pageLocale = getLocaleFromPathname(
      get(data, "url.pathname") as string | undefined
    );
    if (pageLocale) {
      actionParams.pageLocale = pageLocale;
    }
  }

  if (hasCustomerId) {
    // Omit consent info once a customer id is present: that implies the email
    // gate was skipped, so the shopper never got the chance to uncheck the
    // opt-in. These can arrive from the optional params or the form values, so
    // both are sanitized.
    delete actionParams.mobileNumber;
    delete actionParams.subscribe;
    delete actionParams.subscribeSms;
  }

  return actionParams;
}
